import clsx from 'clsx'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useState, type FunctionComponent } from 'react'
import { contemberLinkToHref } from '../utilities/contemberLinkToHref'
import { headerLinkToHrefTargetRel } from '../utilities/headerLinkToHref'
import type { HeaderProps } from './Header'
import styles from './HeaderMobileMenu.module.sass'
import { HeaderSubmenuProducts } from './HeaderSubmenuProducts'
import { Icon } from './Icon'
import { useScrollLock } from './ScrollLock'
import { useTranslate } from './contexts/TranslationsContextProvider'

export type HeaderMobileMenuProps = Pick<HeaderProps, 'links'> & {
	isOpen: boolean
	setIsOpen: (isOpen: boolean) => void
	contactPageLink: string | undefined
}

export const HeaderMobileMenu: FunctionComponent<HeaderMobileMenuProps> = ({
	links,
	isOpen,
	setIsOpen,
	contactPageLink,
}) => {
	const [activeLinkWithSubmenu, setActiveLinkWithSubmenu] = useState<string | null>(null)

	const activeLink = useMemo(() => {
		return links?.find((link) => link?.id === activeLinkWithSubmenu) ?? null
	}, [activeLinkWithSubmenu, links])

	const translation = useTranslate()

	// Add scroll lock when menu is open
	useScrollLock(isOpen)

	const router = useRouter()
	// Close hamburger on navigation
	// biome-ignore lint/correctness/useExhaustiveDependencies: only need to run on route change
	useEffect(() => {
		setIsOpen(false)
	}, [router.asPath])

	return (
		<div className={clsx(styles.wrapper, isOpen && styles.is_open)}>
			<div className={styles.header}>
				<button
					type="button"
					className={clsx(styles.back, activeLink && styles.is_visible)}
					onClick={() => setActiveLinkWithSubmenu(null)}
				>
					<Icon name="chevron" />
				</button>
				<div className={styles.title}>{activeLink?.title}</div>
				<button type="button" className={styles.cross} onClick={() => setIsOpen(false)}>
					<Icon name="cross" />
				</button>
			</div>
			<div className={styles.linksWrapper}>
				<ul className={clsx(styles.links, activeLink && styles.is_hidden)}>
					{links?.map((link) => {
						if (!link) return null
						// biome-ignore lint/suspicious/noExplicitAny: cross-schema properties
						const ext: any = link
						const isSubmenu = Boolean(ext.nestedLinks) || ext.linkListItemType === 'products'
						return (
							<li key={link.id} className={styles.item}>
								<Link
									className={styles.link}
									{...headerLinkToHrefTargetRel(link)}
									onClick={(event) => {
										if (isSubmenu) {
											event.preventDefault()
											setActiveLinkWithSubmenu(link.id)
											return
										}
										setIsOpen(false)
									}}
								>
									{link.title ?? contemberLinkToHref(link)}
									{isSubmenu && (
										<div className={styles.chevron}>
											<Icon name="chevron" />
										</div>
									)}
								</Link>
							</li>
						)
					})}
					{contactPageLink && (
						<li className={styles.item}>
							<Link
								className={clsx(styles.link, styles.is_highlighted)}
								href={contactPageLink}
								onClick={() => setIsOpen(false)}
							>
								{translation('header.contactLabel')}
							</Link>
						</li>
					)}
				</ul>

				<ul className={clsx(styles.links, styles.view_nested, !activeLink && styles.is_hidden)}>
					{(() => {
						if (!activeLink) return null
						// biome-ignore lint/suspicious/noExplicitAny: cross-schema properties
						const a: any = activeLink
						if (a.linkListItemType === 'default') {
							return a.nestedLinks?.items?.map(
								// biome-ignore lint/suspicious/noExplicitAny: dynamic nested item type
								(item: any) => {
									const l = item?.link
									if (!l) return null
									return (
										<li key={l.id} className={styles.item}>
											<Link
												className={styles.link}
												{...headerLinkToHrefTargetRel(l)}
												onClick={() => {
													setIsOpen(false)
													setActiveLinkWithSubmenu(null)
												}}
											>
												{l.title ?? contemberLinkToHref(l)}
											</Link>
										</li>
									)
								},
							)
						}
						// biome-ignore lint/style/noUselessElse: explicit else-if retained for readability
						else if (a.linkListItemType === 'products' && a.listProduct?.length > 0) {
							return (
								<HeaderSubmenuProducts
									items={a.listProduct}
									isFullProductsButton={a.isFullProductsButton}
									productsPageLink={{
										id: a.id,
										isTargetBlank: a.isTargetBlank,
										externalLink: a.externalLink,
										internalLink: a.internalLink,
										title: a.title,
										type: a.type,
									}}
									isInHamburgerMenu
								/>
							)
						}
						return null
					})()}
				</ul>
			</div>
		</div>
	)
}
